/**
 * Admin Google sign-in by full-page redirect (spec 2026-10-15). The popup
 * flow cannot report back inside an installed iOS PWA, so the whole window
 * goes to Google and returns to /auth/google with the token in the fragment.
 * The stored state ties the return to the browser that left: a return that
 * lands in another browser (Safari instead of the installed app) finds no
 * state and is refused instead of signing that browser in.
 */
import { jwtDecode } from "jwt-decode";
import { API_CONFIG } from "@/config/api";
import { useAppStore } from "@/core/store/useAppStore";
import type { User } from "@/types";

/** The admin OAuth client. Public by design; the backend accepts only
 * tokens issued to it (verify_google_token). */
export const GOOGLE_CLIENT_ID =
  "885443936057-ncvcej2k1se351sgcpfq3a85a3bgb63g.apps.googleusercontent.com";

export const ADMIN_OAUTH_STORAGE_KEY = "touchpoint_admin_oauth";
export const ADMIN_OAUTH_MAX_AGE_MS = 10 * 60 * 1000;
export const GOOGLE_CALLBACK_PATH = "/auth/google";

const ADMIN_SIGN_IN_ERROR_CODES = ["not_member", "cancelled", "state", "failed"] as const;
export type AdminSignInErrorCode = (typeof ADMIN_SIGN_IN_ERROR_CODES)[number];

export function isAdminSignInErrorCode(value: unknown): value is AdminSignInErrorCode {
  return (
    typeof value === "string" &&
    (ADMIN_SIGN_IN_ERROR_CODES as readonly string[]).includes(value)
  );
}

export class AdminSignInError extends Error {
  readonly code: AdminSignInErrorCode;

  constructor(code: AdminSignInErrorCode, message?: string) {
    super(message ?? code);
    this.name = "AdminSignInError";
    this.code = code;
  }
}

interface StoredAdminOAuth {
  state: string;
  redirect: string | null;
  createdAt: number;
}

function isStoredAdminOAuth(value: unknown): value is StoredAdminOAuth {
  if (typeof value !== "object" || value === null) return false;
  const entry = value as Record<string, unknown>;
  return (
    typeof entry.state === "string" &&
    (entry.redirect === null || typeof entry.redirect === "string") &&
    typeof entry.createdAt === "number"
  );
}

export function buildGoogleAuthorizeUrl(origin: string, state: string): string {
  const params = new URLSearchParams({
    client_id: GOOGLE_CLIENT_ID,
    redirect_uri: `${origin}${GOOGLE_CALLBACK_PATH}`,
    response_type: "token",
    scope: "openid email profile",
    state,
    prompt: "select_account",
    include_granted_scopes: "true",
  });
  return `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
}

function randomState(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
}

/** Leave for Google. `redirect` must already be admin-validated. */
export function startAdminSignIn(redirect: string | null): void {
  const state = randomState();
  const entry: StoredAdminOAuth = { state, redirect, createdAt: Date.now() };
  localStorage.setItem(ADMIN_OAUTH_STORAGE_KEY, JSON.stringify(entry));
  window.location.assign(buildGoogleAuthorizeUrl(window.location.origin, state));
}

export type GoogleCallbackResult =
  | { ok: true; accessToken: string; redirect: string | null }
  | { ok: false; code: AdminSignInErrorCode };

function takeStoredEntry(): StoredAdminOAuth | null {
  try {
    const raw = localStorage.getItem(ADMIN_OAUTH_STORAGE_KEY);
    localStorage.removeItem(ADMIN_OAUTH_STORAGE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    return isStoredAdminOAuth(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

/**
 * Parse Google's return fragment against the stored state. Always consumes
 * the stored entry, so one sign-in round trip is good for one callback.
 */
export function readGoogleCallback(
  hash: string,
  now: number = Date.now()
): GoogleCallbackResult {
  const params = new URLSearchParams(hash.startsWith("#") ? hash.slice(1) : hash);
  const stored = takeStoredEntry();

  const error = params.get("error");
  if (error === "access_denied") return { ok: false, code: "cancelled" };

  const state = params.get("state");
  if (
    !stored ||
    !state ||
    stored.state !== state ||
    now - stored.createdAt > ADMIN_OAUTH_MAX_AGE_MS
  ) {
    return { ok: false, code: "state" };
  }
  if (error) return { ok: false, code: "failed" };

  const accessToken = params.get("access_token");
  if (!accessToken) return { ok: false, code: "failed" };
  return { ok: true, accessToken, redirect: stored.redirect };
}

interface GoogleUserInfo {
  id: string;
  name: string;
  email: string;
  picture: string;
}

interface ExchangeResponse {
  accessToken: string;
  refreshToken: string;
  user: { id: string; name: string; email: string; picture: string };
}

/**
 * Swap a Google access token for the admin session and store it. Moved from
 * the retired inline login form: tokeninfo carries no name or picture, so userinfo
 * still fills them.
 */
export async function completeAdminSignIn(googleAccessToken: string): Promise<User> {
  const userInfoResponse = await fetch("https://www.googleapis.com/oauth2/v2/userinfo", {
    headers: { Authorization: `Bearer ${googleAccessToken}` },
  });
  if (!userInfoResponse.ok) {
    throw new AdminSignInError("failed", "Failed to fetch user information from Google");
  }
  const userInfo: GoogleUserInfo = await userInfoResponse.json();

  const exchangeResponse = await fetch(`${API_CONFIG.URL}/auth/exchange`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${googleAccessToken}`,
    },
  });
  if (exchangeResponse.status === 403) throw new AdminSignInError("not_member");
  if (!exchangeResponse.ok) {
    throw new AdminSignInError("failed", `Token exchange failed (${exchangeResponse.status})`);
  }
  const exchanged: ExchangeResponse = await exchangeResponse.json();

  let expiresAt: number | undefined;
  try {
    expiresAt = jwtDecode<{ exp: number }>(exchanged.accessToken).exp * 1000;
  } catch {
    expiresAt = undefined;
  }

  const user: User = {
    id: exchanged.user.id || userInfo.id,
    name: exchanged.user.name || userInfo.name,
    email: exchanged.user.email || userInfo.email,
    picture: exchanged.user.picture || userInfo.picture,
    token: "",
    accessToken: exchanged.accessToken,
    refreshToken: exchanged.refreshToken,
    expiresAt,
  };
  useAppStore.getState().setUser(user);
  return user;
}
